/* eslint-disable react/no-unstable-nested-components */
import { createColumnHelper } from "@tanstack/react-table";
import CompanyCorporateActionsView from "components/company/v3/CompanyCorporateActionsView";
import classNames from "components/ui/classNames";
import formatCurrency from "components/utils/formatCurrency";
import getYear from "date-fns/getYear";
import type { IGlobalCorporateActionsQuery } from "lib/queries/__generated__/GlobalCorporateActionsQuery.generated";
import formatDateOrReturnHyphen from "lib/utils/formatDateOrReturnHyphen";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import parseDateYmd from "lib/utils/parseDateYmd";
import { useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import type { IRightsIssuesCorporateAction } from "types/graphqlTypes";

interface RightsIssuesContentProps {
  data: IGlobalCorporateActionsQuery["rightsIssues"];
  isRenderPageDefault: boolean;
}
export default function RightsIssuesContent({ data, isRenderPageDefault }: RightsIssuesContentProps) {
  const [filterRightsIssuesByYear, setFilterRightsIssuesByYear] = useState<number | null>(null);
  const intl = useIntl();

  const columnHelper = createColumnHelper<IRightsIssuesCorporateAction>();

  const availableYears = useMemo(
    () => [
      ...new Set(
        data
          .map((rightsIssues) => {
            if (rightsIssues.excludingDate) {
              return getYear(parseDateYmd(rightsIssues.excludingDate));
            }

            return undefined;
          })
          .filter(notUndefinedOrNull),
      ),
    ],
    [data],
  );

  const filteredByYear = useMemo(
    () =>
      data?.filter((node) => {
        if (!filterRightsIssuesByYear) {
          return true;
        }

        return getYear(parseDateYmd(node.excludingDate)) === filterRightsIssuesByYear;
      }),
    [data, filterRightsIssuesByYear],
  );
  const hasPayDate = filteredByYear?.some((node) => node.payDate);

  const columns = [
    columnHelper.accessor("recordDate", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600 dark:text-slate-300">
          <FormattedMessage defaultMessage="Data-com" id="+YErWR" />
        </div>
      ),
      cell: (info) => (
        <div
          className={classNames(
            "whitespace-nowrap",
            "px-4 py-1.5 text-sm font-normal",
            "text-slate-600 dark:text-slate-300",
          )}
        >
          <span>{formatDateOrReturnHyphen(intl, info.getValue())}</span>
        </div>
      ),
    }),
    hasPayDate
      ? columnHelper.accessor("payDate", {
          header: () => (
            <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600 dark:text-slate-300">
              <FormattedMessage defaultMessage="Pagamento" id="23+rmI" />
            </div>
          ),
          cell: (info) => (
            <div
              className={classNames(
                "whitespace-nowrap",
                "px-4 py-1.5 text-sm font-normal",
                "text-slate-600 dark:text-slate-300",
              )}
            >
              <span>{formatDateOrReturnHyphen(intl, info.getValue())}</span>
            </div>
          ),
        })
      : null,
    columnHelper.accessor("subscriptionPrice", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600 dark:text-slate-300">
          <FormattedMessage defaultMessage="Subscrição (R$)" id="x/NIcW" />
        </div>
      ),
      cell: (info) => {
        const price = info.getValue();
        return (
          <div
            className={classNames(
              "whitespace-nowrap",
              "px-4 py-1.5 text-sm font-normal",
              "text-slate-600 dark:text-slate-300",
            )}
          >
            <span>{price ? formatCurrency(price, 0, 2) : "-"}</span>
          </div>
        );
      },
    }),
    columnHelper.accessor("newShare", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600 dark:text-slate-300">
          <FormattedMessage defaultMessage="Subscrição (%)" id="EbMyht" />
        </div>
      ),
      cell: (info) => {
        const newShare = info.getValue();

        return (
          <div
            className={classNames(
              "whitespace-nowrap",
              "px-4 py-1.5 text-sm font-normal",
              "text-slate-600 dark:text-slate-300",
            )}
          >
            <span>{newShare ? formatCurrency(newShare * 100, 0, 1) : "-"}%</span>
          </div>
        );
      },
    }),
  ].filter(notUndefinedOrNull);

  return (
    <CompanyCorporateActionsView<IRightsIssuesCorporateAction>
      data={filteredByYear || []}
      columns={columns}
      availableYears={availableYears}
      filterByYear={filterRightsIssuesByYear}
      setFilterByYear={setFilterRightsIssuesByYear}
      isRenderPageDefault={isRenderPageDefault}
    />
  );
}
